import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../../components/LegalPage';
import legalConfig from '../../lib/legalConfig';
import { openCookieSettings } from '../../components/CookieConsent';
import { useLanguage } from '../../context/LanguageContext';

const META_DESC =
  'CosmoEstates Cookie Policy: how we use cookies, localStorage, and sessionStorage on the Cyprus property portal, including the "recently viewed" feature, and how to control consent.';

export default function CookiesPage() {
  const { t } = useLanguage();
  return (
    <LegalPage
      title={t('legal.docs.cookiesTitle')}
      effectiveDate={legalConfig.effectiveDate}
      metaTitle="Cookie Policy | CosmoEstates"
      metaDescription={META_DESC}
    >
      <div className="legal-meta">
        <p>
          <strong>CosmoEstates</strong> — {legalConfig.entityName}
        </p>
        <p>
          <strong>Website:</strong>{' '}
          <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a>
        </p>
        <p>
          <strong>Contact:</strong> {legalConfig.privacyEmail}
        </p>
      </div>

      <p className="legal-draft-note">
        Draft for review by Cyprus-qualified counsel. Not legal advice.
      </p>

      <h2>1. What this policy covers</h2>
      <p>
        This policy explains how CosmoEstates uses cookies and similar technologies — including
        browser <strong><code>localStorage</code></strong> and{' '}
        <strong><code>sessionStorage</code></strong> — when you use the Platform. It should be
        read with our <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <h2>2. What are cookies and similar technologies</h2>
      <ul>
        <li>
          <strong>Cookies</strong> are small text files placed on your device by a website.
        </li>
        <li>
          <strong><code>localStorage</code></strong> stores data in your browser that persists
          until it expires or is cleared.
        </li>
        <li>
          <strong><code>sessionStorage</code></strong> stores data only for the current browser
          tab/session.
        </li>
      </ul>
      <p>
        We use these to make the Platform work, to remember your activity, and to improve the
        service.
      </p>

      <h2>3. How we use them</h2>
      <div className="legal-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Examples on CosmoEstates</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Strictly necessary</strong>
              </td>
              <td>Login/session, security, basic functionality</td>
              <td>Required for the site to work; cannot be switched off</td>
            </tr>
            <tr>
              <td>
                <strong>Functional</strong>
              </td>
              <td>
                "Recently viewed" and "Similar to what you viewed" (stored in{' '}
                <code>localStorage</code>, with <code>sessionStorage</code> used to avoid
                double-counting the same listing in a tab); remembering preferences
              </td>
              <td>Personalised experience</td>
            </tr>
            <tr>
              <td>
                <strong>Analytics</strong>
              </td>
              <td>Anonymous usage statistics to understand which pages and features are used</td>
              <td>Understand and improve the Platform</td>
            </tr>
            <tr>
              <td>
                <strong>Advertising</strong>
              </td>
              <td>Not currently used</td>
              <td>Show and measure ads</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>About the "recently viewed" feature</h3>
      <p>
        When you spend a few seconds on a property page, we record that listing in your
        browser's <code>localStorage</code> (and in your account if you are logged in) so we can
        show "recently viewed" and "similar to what you viewed". We keep up to the most recent
        10 listings. <code>localStorage</code> entries clear after 30 days. Owners viewing their
        own listings are not counted.
      </p>
      <div className="legal-callout">
        <p>
          ⚠️ <strong>Known issue to flag/fix:</strong> local viewing history is currently{' '}
          <strong>not cleared on logout</strong>, so a later anonymous user on the same
          browser/device could see the previous user's history. We recommend clearing this on
          logout. Until that is done, be aware of it on shared devices.
        </p>
      </div>

      <h2>4. Third-party cookies</h2>
      <p>
        Some cookies may be set by third parties whose services we use (for example payment or
        mapping providers). We do not control these. Please review those providers' own
        cookie/privacy notices.
      </p>

      <h2>5. Your choices and consent</h2>
      <p>
        5.1 Where required by law, we ask for your consent before setting non-essential
        (functional, analytics, advertising) cookies, through our cookie banner. Strictly
        necessary cookies do not require consent.
      </p>
      <p>
        5.2 You can change or withdraw your consent at any time using the cookie settings
        button:{' '}
        <button
          type="button"
          className="legal-inline-button"
          onClick={openCookieSettings}
        >
          Manage cookie preferences
        </button>
        .
      </p>
      <p>
        5.3 You can also control cookies through your browser settings — including blocking or
        deleting cookies and clearing <code>localStorage</code>/<code>sessionStorage</code>.
        Note that blocking some cookies may affect how the Platform works.
      </p>
      <h2>6. Changes</h2>
      <p>
        We may update this policy. The current version is always available on this page with
        its effective date.
      </p>

      <h2>7. Contact</h2>
      <p>Questions: {legalConfig.privacyEmail}.</p>
    </LegalPage>
  );
}
